import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Stats from '../../components/tagihan/stats';

import { useDarkMode } from '../../components/darkmode/DarkModeContext';
import { useAuth } from '../../pages/auth/AuthContext';
import Footer from '../../components/footer/index';

import { 
  CreditCardIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ReceiptPercentIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

// API Configuration
const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';

const BillingPage = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('semua');
  const [dateFilter, setDateFilter] = useState('semua');
  const [selectedBill, setSelectedBill] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const { isDarkMode } = useDarkMode();
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Helper function untuk mendapatkan data user yang konsisten (same as MyPackages)
  const getActualUserData = () => {
    if (!user) return null;
    
    // Handle nested user structure
    if (user.user && typeof user.user === 'object') {
      if (user.user.profile && typeof user.user.profile === 'object') {
        return user.user.profile;
      }
      return user.user;
    }
    
    // Handle direct profile structure
    if (user.profile && typeof user.profile === 'object' && !user.email && !user.fullName) {
      return user.profile;
    }
    
    // Handle direct user data
    return user;
  };

  const userData = getActualUserData();
  const authToken = token || sessionStorage.getItem('auth_token') || sessionStorage.getItem('token');
  const userId = userData?._id || userData?.id || null;

  // Enhanced fetch function with better error handling (following MyPackages pattern)
  const fetchBills = async () => {
    setLoading(true);
    setError(null);

    try {
      let transformedBills = [];
      
      console.log('Fetching bills for user:', {
        userId,
        userEmail: userData?.email,
        userPhone: userData?.phone,
        isAuthenticated,
        hasToken: !!authToken
      });

      // Only fetch if user is authenticated
      if (!isAuthenticated) {
        // console.log('User not authenticated, skipping fetch');
        setError('Silakan login untuk melihat tagihan Anda');
        setBills([]);
        return;
      }

      // Try authenticated endpoint first (similar to my-orders)
      if (authToken && userId) {
        try {
          const response = await fetch(`${API_BASE_URL}/billings/my-billings`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            credentials: 'include'
          });

          // console.log('My-billings response status:', response.status);

          if (response.ok) {
            const result = await response.json();
            // console.log('My-billings API response:', result);
            
            if (result.success && result.data && result.data.length > 0) {
              transformedBills = transformBillsData(result.data);
              // console.log('Transformed bills from my-billings:', transformedBills);
            }
          }
        } catch (authError) {
          console.error('Authenticated request failed:', authError);
        }
      }

      // Try customer endpoint if no data from authenticated endpoint
      if (transformedBills.length === 0 && (userData?.phone || userData?.email)) {
        try {
          const queryParams = new URLSearchParams();
          if (userData.phone) queryParams.append('phone', userData.phone);
          if (userData.email) queryParams.append('email', userData.email);
          
          // Add filters if not 'semua'
          if (statusFilter !== 'semua') queryParams.append('status', statusFilter);
          if (dateFilter !== 'semua') queryParams.append('period', dateFilter);
          
          const customerUrl = `${API_BASE_URL}/billings/customer?${queryParams.toString()}`;
          // console.log('Trying customer endpoint:', customerUrl);

          const response = await fetch(customerUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          // console.log('Customer billings response status:', response.status);

          if (response.ok) {
            const result = await response.json();
            // console.log('Customer billings API response:', result);
            
            if (result.success && result.data && result.data.length > 0) {
              transformedBills = transformBillsData(result.data);
              // console.log('Transformed bills from customer endpoint:', transformedBills);
            }
          }
        } catch (customerError) {
          console.error('Customer endpoint failed:', customerError);
        }
      }

      // If no data found, show appropriate message
      if (transformedBills.length === 0) {
        setError('Tidak ada tagihan ditemukan. Tagihan akan muncul setelah Anda memiliki paket aktif.');
      }

      setBills(transformedBills);

    } catch (error) {
      console.error('Error fetching bills:', error);
      setError('Gagal mengambil data tagihan. Silakan coba lagi.');
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  // Transform bills data from API response
  const transformBillsData = (billsData) => {
    // console.log('Transforming bills:', billsData);
    
    return billsData.map((bill, index) => {
      // console.log(`Processing bill ${index + 1}:`, bill);
      
      // Extract package info from different possible locations
      const packageInfo = bill.packageSnapshot || bill.package || {};
      const customerInfo = bill.customer || {};
      const addressInfo = bill.address || {};
      const pricingInfo = bill.pricing || {};
      const paymentInfo = bill.payment || {};
      const billingInfo = bill.billing || {};

      const transformedBill = {
        _id: bill._id || bill.id || `bill_${index}`,
        invoiceNumber: bill.invoiceNumber || `INV-${Date.now()}-${index}`,
        packageSnapshot: {
          name: packageInfo.name || 'Paket Internet',
          type: packageInfo.type || 'broadband',
          speed: packageInfo.speed || 'Unknown'
        },
        customer: {
          fullName: customerInfo.fullName || customerInfo.name || 'Customer',
          phone: customerInfo.phone || '',
          email: customerInfo.email || ''
        },
        address: {
          fullAddress: typeof addressInfo === 'string' ? addressInfo : addressInfo.fullAddress || 'Alamat tidak tersedia'
        },
        billing: {
          period: billingInfo.period || formatBillingPeriod(bill.createdAt),
          billingDate: billingInfo.billingDate || bill.createdAt,
          dueDate: billingInfo.dueDate || calculateDueDate(bill.createdAt)
        },
        pricing: {
          amount: pricingInfo.amount || pricingInfo.packagePrice || 0,
          discount: pricingInfo.discount || 0,
          tax: pricingInfo.tax || (pricingInfo.amount || 0) * 0.1,
          lateFee: pricingInfo.lateFee || 0,
          totalAmount: pricingInfo.totalAmount || calculateTotal(pricingInfo)
        },
        payment: {
          status: mapPaymentStatus(paymentInfo.status || bill.status),
          paidAmount: paymentInfo.paidAmount || 0,
          paidDate: paymentInfo.paidDate,
          paymentMethod: paymentInfo.paymentMethod,
          paymentReference: paymentInfo.paymentReference
        },
        paymentHistory: bill.paymentHistory || [],
        description: bill.description || bill.notes || packageInfo.description,
        createdAt: bill.createdAt,
        updatedAt: bill.updatedAt
      };

      // console.log(`Transformed bill ${index + 1}:`, transformedBill);
      return transformedBill;
    });
  };

  // Helper functions
  const formatBillingPeriod = (date) => {
    if (!date) return 'Unknown';
    const d = new Date(date);
    return d.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
  };

  const calculateDueDate = (billingDate) => {
    if (!billingDate) return null;
    const date = new Date(billingDate);
    date.setMonth(date.getMonth() + 1);
    return date.toISOString();
  };

  const calculateTotal = (pricing) => {
    const amount = pricing.amount || pricing.packagePrice || 0;
    const discount = pricing.discount || 0;
    const tax = pricing.tax || amount * 0.1;
    const lateFee = pricing.lateFee || 0;
    return amount - discount + tax + lateFee;
  };

  const mapPaymentStatus = (status) => {
    const statusMap = {
      'pending': 'pending',
      'paid': 'paid',
      'partial': 'partial',
      'overdue': 'overdue',
      'failed': 'failed',
      'cancelled': 'cancelled',
      'unpaid': 'pending'
    };
    return statusMap[status] || 'pending';
  };

  // Guest lookup function with direct API call (kept for non-authenticated users)
  const handleGuestLookup = async (phone, email) => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      if (phone) queryParams.append('phone', phone);
      if (email) queryParams.append('email', email);
      
      const guestUrl = `${API_BASE_URL}/billings/customer?${queryParams.toString()}`;
      // console.log('Guest lookup URL:', guestUrl);

      const response = await fetch(guestUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      // console.log('Guest lookup response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        // console.log('Guest lookup API response:', result);
        
        if (result.success && result.data && result.data.length > 0) {
          const transformedBills = transformBillsData(result.data);
          setBills(transformedBills);
          setError(null);
        } else {
          throw new Error(result.message || 'Tagihan tidak ditemukan');
        }
      } else {
        const errorResult = await response.json();
        throw new Error(errorResult.message || 'Gagal mencari tagihan');
      }
    } catch (err) {
      console.error('Error in guest lookup:', err);
      setError(err.message);
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  // Updated useEffect to follow MyPackages pattern
  useEffect(() => {
    if (isAuthenticated) {
      fetchBills();
    } else {
      setLoading(false);
      setError('Silakan login untuk melihat tagihan Anda atau gunakan pencarian guest di bawah');
      setBills([]);
    }
  }, [user, authToken, isAuthenticated, statusFilter, dateFilter]);

  const getStatusBadge = (status) => {
    const badges = {
      paid: {
        icon: <CheckCircleIcon className="h-4 w-4" />,
        text: 'Lunas',
        classes: 'bg-green-100 text-green-800 border-green-200'
      },
      pending: {
        icon: <ClockIcon className="h-4 w-4" />,
        text: 'Menunggu',
        classes: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      },
      overdue: {
        icon: <ExclamationTriangleIcon className="h-4 w-4" />,
        text: 'Terlambat',
        classes: 'bg-red-100 text-red-800 border-red-200'
      },
      cancelled: {
        icon: <XCircleIcon className="h-4 w-4" />,
        text: 'Dibatalkan',
        classes: 'bg-gray-100 text-gray-800 border-gray-200'
      },
      partial: {
        icon: <ReceiptPercentIcon className="h-4 w-4" />,
        text: 'Sebagian',
        classes: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      failed: {
        icon: <XCircleIcon className="h-4 w-4" />,
        text: 'Gagal',
        classes: 'bg-red-100 text-red-800 border-red-200'
      }
    };

    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${badge.classes}`}>
        {badge.icon}
        <span className="ml-1">{badge.text}</span>
      </span>
    );
  };

  const getTypeIcon = (type) => {
    const icons = {
      broadband: '📶',
      dedicated: '🔗', 
      satelit: '🛰️'
    };
    return icons[type] || '📡';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getFilteredBills = () => {
    return bills.filter(bill => {
      const matchesSearch = 
        bill.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bill.packageSnapshot?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bill.customer?.fullName?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'semua' || bill.payment.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  const openDetailModal = (bill) => {
    setSelectedBill(bill);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedBill(null);
  };

  const getDaysOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const handlePayNow = (bill) => {
    navigate('/pembayaran', { 
      state: { 
        invoiceNumber: bill.invoiceNumber,
        amount: bill.pricing?.totalAmount,
        billData: bill 
      } 
    });
  };

  const refreshData = async () => {
    if (isAuthenticated) {
      await fetchBills();
    }
  };

  // Guest lookup component (for non-authenticated users)
  const GuestLookup = () => {
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      if (phone || email) {
        handleGuestLookup(phone, email);
      }
    };

    return (
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-xl p-6 mb-6`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Cari Tagihan Anda
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="tel"
              placeholder="Nomor telepon"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-[#1E9B4C]`}
            />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-[#1E9B4C]`}
            />
          </div>
          <button
            type="submit"
            disabled={!phone && !email}
            className="w-full px-4 py-2 bg-[#1E9B4C] hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
          >
            Cari Tagihan
          </button>
        </form>
      </div>
    );
  };

  // Bills List Component
  const BillsList = () => {
    const filteredBills = getFilteredBills();

    if (filteredBills.length === 0) {
      return (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <DocumentTextIcon className="mx-auto h-12 w-12 mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">Tidak ada tagihan</h3>
          <p>Tidak ditemukan tagihan yang sesuai dengan kriteria pencarian Anda.</p>
          {isAuthenticated && (
            <div className="mt-4">
              <button 
                onClick={() => navigate('/produk')}
                className="bg-[#1E9B4C] hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
              >
                <CreditCardIcon className="h-5 w-5" />
                <span>Lihat Paket</span>
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {filteredBills.map((bill) => (
          <div
            key={bill._id}
            className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-xl p-6 hover:shadow-md transition-shadow`}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Left Section */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{getTypeIcon(bill.packageSnapshot.type)}</span>
                  <div>
                    <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {bill.packageSnapshot.name}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {bill.invoiceNumber}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Periode</p>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      {bill.billing.period}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Jatuh Tempo</p>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      {formatDate(bill.billing.dueDate)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Section */}
              <div className="flex flex-col items-end gap-3">
                <div className="text-right">
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(bill.pricing.totalAmount)}
                  </p>
                  {bill.payment.status === 'overdue' && (
                    <p className="text-red-500 text-sm mt-1">
                      Terlambat {getDaysOverdue(bill.billing.dueDate)} hari
                    </p>
                  )}
                </div>
                
                <div className="flex items-center gap-3">
                  {getStatusBadge(bill.payment.status)}
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => openDetailModal(bill)}
                      className={`p-2 rounded-lg ${
                        isDarkMode 
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title="Lihat Detail"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    
                    {bill.payment.status === 'pending' || bill.payment.status === 'overdue' ? (
                      <button
                        onClick={() => handlePayNow(bill)}
                        className="bg-[#1E9B4C] hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        Bayar Sekarang
                      </button>
                    ) : (
                      <button
                        className={`p-2 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title="Download Invoice"
                      >
                        <DocumentArrowDownIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Bill Detail Modal
  const BillDetailModal = () => {
    if (!selectedBill) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Detail Tagihan
              </h2>
              <button
                onClick={closeDetailModal}
                className={`p-2 rounded-lg ${
                  isDarkMode 
                    ? 'hover:bg-gray-700 text-gray-300' 
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="space-y-6">
              {/* Invoice Info */}
              <div>
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Informasi Tagihan
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>No. Invoice</p>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>{selectedBill.invoiceNumber}</p>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status</p>
                    <div className="mt-1">{getStatusBadge(selectedBill.payment.status)}</div>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Periode</p>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>{selectedBill.billing.period}</p>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Jatuh Tempo</p>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      {formatDate(selectedBill.billing.dueDate)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Package Info */}
              <div>
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Paket Layanan
                </h3>
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getTypeIcon(selectedBill.packageSnapshot.type)}</span>
                    <div>
                      <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedBill.packageSnapshot.name}
                      </h4>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {selectedBill.packageSnapshot.speed}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div>
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Rincian Biaya
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Biaya Paket</span>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      {formatCurrency(selectedBill.pricing.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Pajak (10%)</span>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      {formatCurrency(selectedBill.pricing.tax)}
                    </span>
                  </div>
                  {selectedBill.pricing.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Diskon</span>
                      <span>-{formatCurrency(selectedBill.pricing.discount)}</span>
                    </div>
                  )}
                  {selectedBill.pricing.lateFee > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Denda Keterlambatan</span>
                      <span>+{formatCurrency(selectedBill.pricing.lateFee)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between font-semibold">
                      <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>Total</span>
                      <span className="text-lg text-[#1E9B4C]">
                        {formatCurrency(selectedBill.pricing.totalAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={closeDetailModal}
                  className={`flex-1 px-4 py-2 border rounded-lg font-medium ${
                    isDarkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Tutup
                </button>
                {(selectedBill.payment.status === 'pending' || selectedBill.payment.status === 'overdue') && (
                  <button
                    onClick={() => handlePayNow(selectedBill)}
                    className="flex-1 bg-[#1E9B4C] hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Bayar Sekarang
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} pt-20`}
        style={{
          backgroundImage: `url('https://telnet.net.id/assets/bg.png')`,
          backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
          backgroundBlendMode: 'overlay',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className={`h-8 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded w-1/4`}></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-md p-6 space-y-4`}>
                  <div className={`h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded`}></div>
                  <div className={`h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded w-2/3`}></div>
                  <div className={`h-20 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded`}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} pt-20`}
      style={{
        backgroundImage: `url('https://telnet.net.id/assets/bg.png')`,
        backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
        backgroundBlendMode: 'overlay',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Tagihan Saya
              </h1>
              <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Kelola dan pantau semua tagihan internet Anda
              </p>
              {userData && (
                <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  User: {userData.fullName || userData.email}
                </p>
              )}
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button 
                onClick={refreshData}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
                disabled={loading}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className={`mb-6 p-4 rounded-xl border ${
            isDarkMode 
              ? 'bg-yellow-500/20 border-yellow-500/50' 
              : 'bg-yellow-100 border-yellow-200'
          }`}>
            <div className="flex items-center space-x-3">
              <ExclamationTriangleIcon className={`h-6 w-6 ${
                isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
              }`} />
              <div>
                <h4 className={`font-semibold ${
                  isDarkMode ? 'text-yellow-300' : 'text-yellow-800'
                }`}>
                  {error.includes('login') ? 'Autentikasi Diperlukan' : 'Peringatan'}
                </h4>
                <p className={`text-sm ${
                  isDarkMode ? 'text-yellow-200' : 'text-yellow-700'
                }`}>
                  {error}
                </p>
                {error.includes('login') && (
                  <button
                    onClick={() => navigate('/login')}
                    className="mt-2 bg-[#1E9B4C] hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    Login Sekarang
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards - Only show if authenticated and has bills */}
        {isAuthenticated && bills.length > 0 && (
          <Stats bills={bills} />
        )}

        {/* Search and Filter - Only show if authenticated and has bills */}
        {isAuthenticated && bills.length > 0 && (
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-xl p-6 mb-6`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cari tagihan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-[#1E9B4C]`}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`px-4 py-2 rounded-lg border ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-gray-50 border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-[#1E9B4C]`}
              >
                <option value="semua">Semua Status</option>
                <option value="pending">Menunggu</option>
                <option value="paid">Lunas</option>
                <option value="overdue">Terlambat</option>
                <option value="partial">Sebagian</option>
              </select>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className={`px-4 py-2 rounded-lg border ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-gray-50 border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-[#1E9B4C]`}
              >
                <option value="semua">Semua Periode</option>
                <option value="bulan-ini">Bulan Ini</option>
                <option value="bulan-lalu">Bulan Lalu</option>
                <option value="3-bulan">3 Bulan Terakhir</option>
              </select>
            </div>
          </div>
        )}

        {/* Guest Lookup - Show if not authenticated */}
        {!isAuthenticated && <GuestLookup />}

        {/* Bills List */}
        {isAuthenticated && bills.length > 0 ? (
          <BillsList />
        ) : isAuthenticated ? (
          // Empty state for authenticated users with no bills
          <div className="text-center py-12">
            <div className={`mx-auto h-24 w-24 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              <DocumentTextIcon className="h-24 w-24" />
            </div>
            <h3 className={`mt-2 text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
              Belum ada tagihan
            </h3>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Anda belum memiliki tagihan. Tagihan akan muncul setelah Anda memiliki paket aktif.
            </p>
            <div className="mt-6">
              <button 
                onClick={() => navigate('/produk')}
                className="bg-[#1E9B4C] hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
              >a
                <CreditCardIcon className="h-5 w-5" />
                <span>Lihat Paket</span>
              </button>
            </div>
          </div>
        ) : bills.length > 0 ? (
          <BillsList />
        ) : null}

        {/* Bill Detail Modal */}
        {showDetailModal && <BillDetailModal />}
      </div>
      <Footer />
    </div>
  );
};

export default BillingPage;